import { useMemo } from "react";
import { Check, ChevronLeft, ChevronRight, Copy, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface SlotConfiguratorProps {
  slotIndex: number;
  totalSlots: number;
  scenePacks: Array<{ id: string; title: string; imageUrl: string }>;
  cameos: Array<{ id: string; name: string; imageUrl: string }>;
  isVideo?: boolean;
  currentConfig: {
    scenePackId: string | null;
    cameoIds: string[];
    style: string | null;
    goofyPrompt: string | null;
  };
  onConfigChange: (config: {
    scenePackId: string;
    cameoIds: string[];
    style: string;
    goofyPrompt: string | null;
  }) => void;
  onApplyToAll: () => void;
  onPrev: () => void;
  onNext: () => void;
  canGoPrev: boolean;
  canGoNext: boolean;
}

const STYLE_OPTIONS = [
  { id: "photoreal", label: "Photoreal" },
  { id: "cinematic", label: "Cinematic" },
  { id: "anime", label: "Anime" },
  { id: "3d", label: "3D" },
  { id: "comic", label: "Comic" },
  { id: "watercolor", label: "Watercolor" },
  { id: "retro", label: "Retro" },
];

export function SlotConfigurator({
  slotIndex,
  totalSlots,
  scenePacks,
  cameos,
  isVideo = false,
  currentConfig,
  onConfigChange,
  onApplyToAll,
  onPrev,
  onNext,
  canGoPrev,
  canGoNext,
}: SlotConfiguratorProps) {
  // Safe defaults
  const scenePackId = currentConfig.scenePackId || scenePacks[0]?.id;
  const cameoIds = currentConfig.cameoIds || [];
  const style = currentConfig.style || "photoreal";
  const goofyPrompt = currentConfig.goofyPrompt || "";

  // Video slots limited to 1 cameo, image slots allow 3
  const maxCameos = isVideo ? 1 : 3;

  const handleSceneSelect = (id: string) => {
    onConfigChange({
      scenePackId: id,
      cameoIds,
      style,
      goofyPrompt: goofyPrompt || null,
    });
  };

  const handleCameoToggle = (id: string) => {
    const isSelected = cameoIds.includes(id);
    let newCameos = [...cameoIds];

    if (isSelected) {
      newCameos = newCameos.filter((c) => c !== id);
    } else {
      if (newCameos.length >= maxCameos) return;
      newCameos.push(id);
    }

    onConfigChange({
      scenePackId,
      cameoIds: newCameos,
      style,
      goofyPrompt: goofyPrompt || null,
    });
  };

  const handleStyleSelect = (id: string) => {
    onConfigChange({
      scenePackId,
      cameoIds,
      style: id,
      goofyPrompt: goofyPrompt || null,
    });
  };

  const handlePromptChange = (val: string) => {
    onConfigChange({
      scenePackId,
      cameoIds,
      style,
      goofyPrompt: val || null,
    });
  };

  // Progress dots
  const progressDots = useMemo(() => {
    return Array.from({ length: totalSlots }).map((_, i) => (
      <div
        key={i}
        className={cn(
          "h-1.5 w-1.5 rounded-full transition-all",
          i === slotIndex ? "w-4 bg-white" : "bg-white/20",
        )}
      />
    ));
  }, [totalSlots, slotIndex]);

  return (
    <div className="flex flex-col gap-6">
      {/* Header / Nav */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">{progressDots}</div>
          <span className="ml-3 font-mono text-sm text-white/50">
            {slotIndex + 1}/{totalSlots}
          </span>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={onPrev}
            disabled={!canGoPrev}
            className="border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={onNext}
            disabled={!canGoNext}
            className="border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Card className="border-white/10 bg-white/5 text-white shadow-2xl backdrop-blur-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-xl font-medium tracking-tight">
            {isVideo ? "Configure Video" : `Configure Image #${slotIndex + 1}`}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onApplyToAll}
            className="h-8 gap-2 text-xs uppercase tracking-wider text-white/60 hover:bg-white/10 hover:text-white"
          >
            <Copy className="h-3 w-3" />
            Apply to empty
          </Button>
        </CardHeader>

        <CardContent className="space-y-8">
          {/* Scene Packs */}
          <section className="space-y-3">
            <label className="text-xs font-medium uppercase tracking-wider text-white/40">
              Scene Pack
            </label>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {scenePacks.map((pack) => {
                const isSelected = scenePackId === pack.id;
                return (
                  <button
                    key={pack.id}
                    onClick={() => handleSceneSelect(pack.id)}
                    className={cn(
                      "group relative aspect-video w-full overflow-hidden rounded-xl border text-left transition-all active:scale-95",
                      isSelected
                        ? "border-white ring-2 ring-white/20"
                        : "border-white/10 opacity-70 hover:opacity-100",
                    )}
                  >
                    <img
                      src={pack.imageUrl}
                      alt={pack.title}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 p-3">
                      <div className="text-sm font-semibold text-white">
                        {pack.title}
                      </div>
                    </div>
                    {isSelected && (
                      <div className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white text-black shadow-lg">
                        <Check className="h-3.5 w-3.5" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Cameos */}
          {cameos.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium uppercase tracking-wider text-white/40">
                  Cameos{" "}
                  <span className="normal-case opacity-50">
                    (Max {maxCameos})
                  </span>
                </label>
                <span className="text-xs text-white/40">
                  {cameoIds.length}/{maxCameos} selected
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
                {cameos.map((cameo) => {
                  const isSelected = cameoIds.includes(cameo.id);
                  const isDisabled =
                    !isSelected && cameoIds.length >= maxCameos;
                  return (
                    <button
                      key={cameo.id}
                      onClick={() => handleCameoToggle(cameo.id)}
                      disabled={isDisabled}
                      className={cn(
                        "group relative aspect-square w-full overflow-hidden rounded-xl border text-left transition-all active:scale-95",
                        isSelected
                          ? "border-white ring-2 ring-white/20"
                          : "border-white/10 opacity-70 hover:opacity-100",
                        isDisabled && "cursor-not-allowed opacity-30",
                      )}
                    >
                      <img
                        src={cameo.imageUrl}
                        alt={cameo.name}
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 w-full p-2 text-center">
                        <div className="truncate text-xs font-medium text-white">
                          {cameo.name}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-white text-black shadow-lg">
                          <Check className="h-3 w-3" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Style & Prompt */}
          <div className="grid gap-8 md:grid-cols-2">
            <section className="space-y-3">
              <label className="text-xs font-medium uppercase tracking-wider text-white/40">
                Visual Style
              </label>
              <div className="flex flex-wrap gap-2">
                {STYLE_OPTIONS.map((opt) => {
                  const isSelected = style === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => handleStyleSelect(opt.id)}
                      className={cn(
                        "rounded-full border px-4 py-2 text-sm font-medium transition-all active:scale-95",
                        isSelected
                          ? "border-white bg-white text-black shadow-[0_0_15px_-3px_rgba(255,255,255,0.3)]"
                          : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white",
                      )}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="space-y-3">
              <label className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-white/40">
                <Wand2 className="h-3 w-3" />
                Custom Twist
              </label>
              <div className="relative">
                <Input
                  value={goofyPrompt}
                  onChange={(e) => handlePromptChange(e.target.value)}
                  placeholder="e.g. make it spooky, add confetti..."
                  className="h-12 border-white/10 bg-black/20 text-white placeholder:text-white/20 focus-visible:border-white/30 focus-visible:ring-0"
                />
                {goofyPrompt && (
                  <button
                    onClick={() => handlePromptChange("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/40 hover:text-white"
                  >
                    Clear
                  </button>
                )}
              </div>
            </section>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
